/**
 * i18n - Internationalization Module
 *
 * Clean, scalable internationalization system for the frontend.
 *
 * Usage:
 *   import { useTranslation } from '../i18n';
 *
 *   function MyComponent() {
 *     const { t } = useTranslation();
 *     return <h1>{t('dashboard.title')}</h1>;
 *   }
 *
 * Adding a new language:
 *   1. Create a new file in /locales (e.g., fr.ts)
 *   2. Copy the structure from en.ts
 *   3. Add to SUPPORTED_LANGUAGES below
 *   4. Import and add to translations object
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { en } from './locales/en';
import type { TranslationKeys } from './locales/en';
import { de } from './locales/de';
import { logger } from '../lib/logger';

// ============================================================================
// Types
// ============================================================================

export type Language = 'en' | 'de';

export interface LanguageOption {
  code: Language;
  name: string;
  nativeName: string;
  flag: string;
}

// ============================================================================
// Configuration
// ============================================================================

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  // Add more languages here as needed:
  // { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  // { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
];

export const DEFAULT_LANGUAGE: Language = 'en';
export const STORAGE_KEY = 'app_language';

// Translation objects indexed by language code
const translations: Record<Language, TranslationKeys> = {
  en,
  de: de as unknown as TranslationKeys,
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get nested value from object using dot notation
 * e.g., getValue({ a: { b: 'hello' }}, 'a.b') => 'hello'
 */
function getValue(obj: any, path: string): string | undefined {
  const keys = path.split('.');
  let result = obj;

  for (const key of keys) {
    if (result && typeof result === 'object' && key in result) {
      result = result[key];
    } else {
      return undefined;
    }
  }

  return typeof result === 'string' ? result : undefined;
}

/**
 * Replace template variables in string
 * e.g., interpolate('Hello {{name}}', { name: 'World' }) => 'Hello World'
 */
function interpolate(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;

  return text.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    return vars[key]?.toString() ?? `{{${key}}}`;
  });
}

/**
 * Detect browser language preference
 */
function detectBrowserLanguage(): Language {
  if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE;

  const browserLang = navigator.language?.split('-')[0];
  const supported = SUPPORTED_LANGUAGES.find(l => l.code === browserLang);

  return supported?.code ?? DEFAULT_LANGUAGE;
}

/**
 * Get stored language preference
 */
function getStoredLanguage(): Language | null {
  if (typeof localStorage === 'undefined') return null;

  const stored = localStorage.getItem(STORAGE_KEY);
  const supported = SUPPORTED_LANGUAGES.find(l => l.code === stored);

  return supported?.code ?? null;
}

// ============================================================================
// Context
// ============================================================================

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  languages: LanguageOption[];
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

// ============================================================================
// Provider
// ============================================================================

interface I18nProviderProps {
  children: React.ReactNode;
  defaultLanguage?: Language;
}

export function I18nProvider({ children, defaultLanguage }: I18nProviderProps) {
  const [language, setLanguageState] = useState<Language>(() => {
    // Priority: stored > default prop > browser detection > fallback
    return getStoredLanguage() ?? defaultLanguage ?? detectBrowserLanguage();
  });

  // Persist language change
  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
    // Update HTML lang attribute for accessibility
    document.documentElement.lang = lang;
  }, []);

  // Set initial HTML lang attribute
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Translation function
  const t = useCallback((key: string, vars?: Record<string, string | number>): string => {
    const translation = getValue(translations[language], key);

    if (translation === undefined) {
      // Fallback to English
      const fallback = getValue(translations.en, key);
      if (fallback === undefined) {
        // Return key if no translation found (helps identify missing translations)
        logger.warn(`Missing translation: ${key}`);
        return key;
      }
      return interpolate(fallback, vars);
    }

    return interpolate(translation, vars);
  }, [language]);

  const value: I18nContextType = {
    language,
    setLanguage,
    t,
    languages: SUPPORTED_LANGUAGES,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Main hook for translations
 */
export function useTranslation() {
  const context = useContext(I18nContext);

  if (context === undefined) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }

  return context;
}

/**
 * Alias for backwards compatibility with existing code
 */
export const useLanguage = useTranslation;

// ============================================================================
// Exports
// ============================================================================

export type { TranslationKeys };
export { translations };
